import renderAsAllDay from 'owa-calendar-types/lib/utils/renderAsAllDay';
import resolveAllDayColumnsWithItems from './resolveAllDayColumnsWithItems';
import resolveRowsWithItems from './resolveRowsWithItems';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import compareCalendarEvents from 'owa-calendar-types/lib/utils/compareCalendarEvents';
import CalendarSurfaceStore from '../store/schema/CalendarSurfaceStore';
import { ColumnProvider } from '../interfaces/ColumnProvider';
import { computed } from 'mobx';
import { createSurfaceItem } from './createSurfaceItem';
import { FreeBusyConstants } from 'owa-freebusy-style';
import { groupByStartColumn } from './groupByStartColumn';
import { LayoutInterval } from '../interfaces/LayoutInterval';
import { merge } from './merge';
import { SurfaceItem } from '../interfaces/SurfaceItem';

/**
 * This interface defines a data contract for day/week content generation
 */
export interface ColumnItemsProvider {
    inDayItems: SurfaceItem[];
    columnInDayItems: SurfaceItem[][];
    allDayRows: SurfaceItem[][];
    busyStripModels: SurfaceItem[][];
    tabOrder: string[];
}

/**
 * Implements data provider for Day/Week content generation
 */
export class SurfaceColumnItemsProvider implements ColumnItemsProvider {
    constructor(
        private store: CalendarSurfaceStore,
        private columnProvider: ColumnProvider,
        private getItems: () => CalendarEvent[]
    ) {}

    /**
     * Gets in-day surface items grouped by columns
     */
    @computed
    get columnInDayItems(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return groupByStartColumn(this.inDayItems, columnCount, columnLength);
    }

    /**
     * Gets all-day surface items grouped by columns
     */
    @computed
    private get columnAllDayItems(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return groupByStartColumn(this.allDayItems, columnCount, columnLength);
    }

    /**
     * Gets all day surface items grouped by rows
     */
    @computed
    get allDayRows(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return resolveRowsWithItems(this.allDayItems, columnCount, columnLength).VisibleRows;
    }

    /**
     * Gets all days surface items
     */
    @computed
    get allDayItems(): SurfaceItem[] {
        let results: SurfaceItem[] = [];
        this.items
            .filter(itemModel => renderAsAllDay(itemModel))
            .forEach(model => {
                let intervals: LayoutInterval[] = this.columnProvider.getColumnIntervals(model);
                if (intervals.length > 0) {
                    // create item only if in range.
                    results.push(createSurfaceItem(merge(intervals), model.ItemId.Id, model));
                }
            });
        return results;
    }

    /**
     * Gets in-day surface items
     */
    @computed
    get inDayItems(): SurfaceItem[] {
        let results: SurfaceItem[] = [];
        this.items
            .filter(itemModel => !renderAsAllDay(itemModel))
            .forEach(model => {
                let intervals: LayoutInterval[] = this.columnProvider.getColumnIntervals(model);
                results.push(
                    ...intervals.map<SurfaceItem>((inst, i) => {
                        return {
                            ...inst,
                            ...{ key: model.ItemId.Id + '_' + i, model: model },
                        };
                    })
                );
            });
        return results;
    }

    /**
     * Gets tab order
     * Tab order is grouped by column.
     * Colunmn group starts with header, then followed by all day events and then by in-day events
     */
    @computed
    get tabOrder(): string[] {
        let results: string[] = [];
        this.columnProvider.columnModels.forEach((model, index) => {
            // push just the number for the id of the column header
            results.push('surfaceHeader_' + index);
            // push all the column data
            results.push(
                ...this.columnAllDayItems[index]
                    .concat(this.columnInDayItems[index])
                    .map(item => item.key)
            );
        });
        return results;
    }

    /**
     * Gets busy strip models for in-day view
     */
    @computed
    get busyStripModels(): SurfaceItem[][] {
        const modelsWithFreeBusyInfo = this.items.filter(
            model =>
                model.FreeBusyType != null &&
                (model.FreeBusyType == FreeBusyConstants.Busy ||
                    model.FreeBusyType == FreeBusyConstants.OOF) &&
                renderAsAllDay(model)
        );

        return resolveAllDayColumnsWithItems(
            modelsWithFreeBusyInfo,
            this.store,
            this.columnProvider
        );
    }

    /**
     * Gets sorted collection of all item models.
     * Filters out items that are completely oot-of-range of current view
     */
    @computed
    get sortedItems(): CalendarEvent[] {
        return this.getItems()
            .filter(model => this.columnProvider.getColumnIntervals(model).length > 0)
            .sort(compareCalendarEvents);
    }

    /**
     * Gets sorted collection of all item models with the colorProvider applied to them
     */
    @computed
    private get items(): CalendarEvent[] {
        return this.sortedItems;
    }
}
